import { useCallback } from 'react';
import { LayoutAnimation } from 'react-native';

import { useLinkTo, useNavigation } from '@react-navigation/native';

import { queryClient } from '@/data';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { BonusEngineAPIClient, useBonusEngineClient, userPromosKeys } from '@/providers';
import { logger } from '@/utils/logging';
import { useMutation } from '@tanstack/react-query';

const LOG_TAG = '[Promo Submission]';

/** New opt-in endpoint — only for newly onboarded verticals (everything except Picks) */
const optInToPromotion = (
    client: BonusEngineAPIClient,
    { userId, bonusEnginePromoId, product }: { userId: string; bonusEnginePromoId: string; product: string }
) =>
    client.fetch(`/users/${userId}/promotions/${bonusEnginePromoId}/opt-in`, {
        method: 'POST',
        body: JSON.stringify({
            product,
            isGenericPromotion: false,
        }),
    });

/** Submissions endpoint — used for generic promos and for Picks segmented promos */
const submitPromo = (
    client: BonusEngineAPIClient,
    {
        userId,
        promoSlug,
        segmented,
        bonusEnginePromoId,
    }: { userId: string; promoSlug: string; segmented: boolean; bonusEnginePromoId?: string }
) =>
    client.fetch('/submissions', {
        method: 'POST',
        body: JSON.stringify({
            accountId: userId,
            gameId: promoSlug,
            // The `answers` object is hardcoded as `{ 1: true }` because that's how it was initially created and it was kept this way.
            answers: { 1: true },
            isGenericPromotion: !segmented,
            promoId: bonusEnginePromoId,
        }),
    });

export const usePromoOptIn = ({
    promoId,
    segmented,
    redirectPath,
    onSuccess,
    bonusEnginePromoId,
    analyticsEvent,
}: {
    promoId: string;
    segmented: boolean;
    redirectPath?: string;
    onSuccess?: () => void;
    bonusEnginePromoId?: string;
    analyticsEvent: AnalyticsEvent;
}) => {
    const submissionMutation = useAddSubmission();
    const navigation = useNavigation();

    const handleOptIn = useCallback(async () => {
        if (useUserStore.getState().guest) {
            navigation.reset({ routes: [{ name: 'Launch' }] });
            return;
        }
        BetrAnalytics.trackProductEvent(analyticsEvent, { promoId });
        try {
            await submissionMutation.mutateAsync({
                promoSlug: promoId,
                segmented: segmented,
                redirectPath: redirectPath,
                bonusEnginePromoId: bonusEnginePromoId,
            });
            LayoutAnimation.easeInEaseOut();
            onSuccess?.();
        } catch (e) {
            logger.debug(LOG_TAG, 'Opt in failed', e);
        }
    }, [
        analyticsEvent,
        promoId,
        navigation,
        submissionMutation,
        segmented,
        redirectPath,
        bonusEnginePromoId,
        onSuccess,
    ]);

    return { optIn: handleOptIn, loading: submissionMutation.isPending };
};

export const useAddSubmission = () => {
    const bonusEngineAPIClient = useBonusEngineClient();
    const linkTo = useLinkTo();

    const handleRedirect = useCallback(
        (promoSlug: string, redirectPath?: string) => {
            if (redirectPath) {
                linkTo('/' + redirectPath + `?promoSlug=${promoSlug}`);
            }
        },
        [linkTo]
    );

    const addSubmission = async ({
        promoSlug,
        segmented = false,
        redirectPath,
        bonusEnginePromoId,
    }: {
        promoSlug: string;
        segmented?: boolean;
        redirectPath?: string;
        bonusEnginePromoId?: string;
    }) => {
        const userId = useUserStore.getState().profile.sub;
        const product = useJurisdictionStore.getState().product;
        const isSweeps = product === Product.Sweeps;

        // Segmented promos must carry a bonus engine promoId for the opt-in endpoint.
        if (segmented && !bonusEnginePromoId) {
            logger.error(LOG_TAG, 'Segmented promo missing bonusEnginePromoId', { promoSlug });
            throw new Error('Failed to add submission');
        }
        try {
            // The /opt-in endpoint is only for newly onboarded verticals (for now Sweeps only); Picks keeps using /submissions.
            if (isSweeps && segmented && bonusEnginePromoId) {
                await optInToPromotion(bonusEngineAPIClient, {
                    userId,
                    bonusEnginePromoId,
                    product: product.toUpperCase(),
                });
            } else {
                await submitPromo(bonusEngineAPIClient, { userId, promoSlug, segmented, bonusEnginePromoId });
            }
            handleRedirect(promoSlug, redirectPath);
            queryClient.invalidateQueries({ queryKey: userPromosKeys.all });
        } catch (err) {
            logger.error('Failed to add submission', err);
            throw err;
        }
    };

    return useMutation({ mutationKey: ['add-submission'], mutationFn: addSubmission });
};
